import React, { useEffect, useState } from 'react';
import { useOnboardingStore } from '../../store';
import { CheckCircle, Mail, Cog, Save, Search, Sparkles, Loader2, ExternalLink, type LucideIcon } from 'lucide-react';
import { useAuth } from '../../contexts/useAuth';
import toast from 'react-hot-toast';
import { canSkipSteps } from '../../config/beta';
import { FinalizeOnboardingResult, finalizeOnboardingLead } from '../../lib/leadCapture';

type ProvisioningStatus = 'idle' | 'saving' | 'provisioning' | 'complete' | 'error';
const CONNECT_APP_URL = 'https://dashboard.scalesmall.ai/connect';

function SocialPageAccessModal({ onClose }: { onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4" role="presentation" onClick={onClose}>
      <div
        className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-xl border border-[#22c55e]/50 bg-scale-darker p-6 text-left shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="social-page-access-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 mb-4">
          <h3 id="social-page-access-title" className="text-xl font-semibold text-white">
            Facebook and LinkedIn page access
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-600 px-3 py-2 text-sm font-semibold text-gray-200 hover:bg-white/10"
          >
            Close
          </button>
        </div>
        <p className="text-sm leading-6 text-gray-300">
          Use two logins in the right places. Your Scale Small AI login gets you into the dashboard connection page.
          The Facebook or LinkedIn login in the popup must be the account that has admin access to the business page.
        </p>
        <div className="my-5 grid gap-3">
          {[
            ['1. Browser profile', 'Open an incognito window or Chrome profile where the Facebook or LinkedIn account with page admin access is signed in.'],
            ['2. Scale Small AI login', 'Go to dashboard.scalesmall.ai and sign in with the normal client dashboard login.'],
            ['3. Connect Platforms', 'Click Connect for Facebook or LinkedIn.'],
            ['4. Platform popup', 'When Facebook or LinkedIn opens, continue with the personal or admin account that manages the business/company page.'],
            ['5. Choose the page', 'If asked, select the business page or company page, approve access, and return to Scale Small AI.']
          ].map(([title, copy]) => (
            <div key={title} className="rounded-lg border border-[#22c55e]/30 border-l-4 border-l-[#22c55e] bg-[#22c55e]/10 p-4">
              <p className="font-semibold text-[#86efac]">{title}</p>
              <p className="mt-1 text-sm leading-5 text-[#d9f99d]">{copy}</p>
            </div>
          ))}
        </div>
        <p className="border-t border-gray-700 pt-4 text-sm leading-6 text-[#bbf7d0]">
          Important: the personal Facebook or LinkedIn account is not your Scale Small AI login. It is only used inside the platform popup so Facebook or LinkedIn can show the pages that account manages.
        </p>
      </div>
    </div>
  );
}

export function Success() {
  const {
    selectedTier,
    selectedServiceTypes,
    setStep,
    selectedServices,
    client,
    textAvailabilityNotificationsAllowed,
  } = useOnboardingStore();
  const { user } = useAuth();
  const [status, setStatus] = useState<ProvisioningStatus>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showPageAccessInfo, setShowPageAccessInfo] = useState(false);
  const [credentialResult, setCredentialResult] = useState<FinalizeOnboardingResult | null>(null);

  useEffect(() => {
    if (!selectedTier && selectedServiceTypes.length === 0 && window.location.pathname !== '/') {
      setStep(1);
    }
  }, [selectedTier, selectedServiceTypes, setStep]);

  const handleSaveAndProvision = async () => {
    try {
      setStatus('saving');
      setErrorMessage(null);
      setCredentialResult(null);

      // In beta mode, just redirect without saving
      if (canSkipSteps()) {
        toast.success('Onboarding complete! Redirecting...');
        setTimeout(() => { window.location.href = 'https://scalesmall.ai'; }, 1000);
        return;
      }

      const email = user?.email || client.email;
      if (!email) {
        toast.error('Add an email before final submit.');
        setStatus('error');
        return;
      }

      const credentials = await finalizeOnboardingLead({
        email,
        businessName: client.businessName,
        contactName: client.contactName,
        phone: client.phone,
        address: client.address,
        selectedServiceTypes,
        selectedTier,
        selectedServices,
        n8nClientId: null,
        textNotificationsAllowed: textAvailabilityNotificationsAllowed,
      });
      setCredentialResult(credentials);
      toast.success('Secure account setup prepared.');
      if (!credentials.client_profile_provisioned) {
        throw new Error('The client profile was not provisioned. Please retry before continuing.');
      }
      const userId = credentials.user_id || user?.id;
      if (!userId) {
        throw new Error('Account was created, but no dashboard user ID was returned.');
      }

      // Step 2: the canonical database finalizer provisions the complete client profile.
      setStatus('provisioning');
      setStatus('complete');
      toast.success('Your account is set up. Next, connect your platforms.');

    } catch (error) {
      console.error('Error during save/provision:', error);
      const msg = error instanceof Error ? error.message : 'Unknown error';
      setErrorMessage(msg);
      toast.error('Something went wrong. Please try again or contact support.');
      setStatus('error');
    }
  };

  if (!selectedTier && selectedServiceTypes.length === 0) {
    return (
      <div className="mx-auto w-full max-w-xl rounded-xl border border-yellow-600/50 bg-yellow-600/10 p-6 text-center">
        <h2 className="text-xl font-semibold text-white">Choose services before finishing</h2>
        <p className="mt-2 text-sm leading-6 text-gray-300">Your saved session does not contain a service selection, so no account changes were submitted.</p>
        <button type="button" onClick={() => setStep(1)} className="mt-5 rounded-md bg-scale-blue px-5 py-3 font-semibold text-scale-dark hover:bg-white">
          Return to service selection
        </button>
      </div>
    );
  }

  const hasMarketing = selectedServiceTypes.some(type => type.id === 'marketing');
  const hasSEO = selectedServiceTypes.some(type => type.id === 'seo-optimization' || type.id === 'seo-gbp-audit');
  const hasAutomation = selectedServiceTypes.some(type => type.id === 'automation');
  const hasPoW = selectedServiceTypes.some(type => type.id === 'proof-of-work');

  const getServiceNotes = () => {
    const notes: { icon: LucideIcon; title: string; description: string }[] = [];

    if (selectedServices && selectedServices.length > 0) {
      selectedServices.forEach((service) => {
        let icon = Mail;
        const title = service.name + ' Setup';
        let description = '';

        if (service.id.includes('ai-audit')) {
          icon = Search;
          description = `${service.name} is saved to your account. Connect Search Console and confirm the website you want audited so the dashboard report can use real source and visibility data.`;
        } else if (service.id.includes('marketing') || service.id.includes('social')) {
          icon = Sparkles;
          description = `${service.name} is saved to your account. Use Connect next to choose the platforms and sources you want Scale Small AI to use.`;
        } else if (service.id.includes('gbp') || service.id.includes('seo')) {
          icon = Search;
          description = `${service.name} is saved to your account. The dashboard setup tracker will show anything still needed before final review.`;
        } else if (service.id.includes('automation')) {
          icon = Cog;
          description = `${service.name} is saved to your account. Use the dashboard tracker to finish required setup details and submit for review.`;
        } else {
          description = `${service.name} is saved to your account. Connect what you want to use, then follow the dashboard tracker.`;
        }

        notes.push({ icon, title, description });
      });
    } else {
      if (hasMarketing || hasPoW) {
        notes.push({
          icon: Sparkles,
          title: 'Marketing & Content Setup',
          description: 'Your selected marketing system is saved. Next, connect the platforms and sources you want to use, then follow the dashboard tracker.'
        });
      }
      if (hasSEO) {
        notes.push({
          icon: Search,
          title: 'SEO & Google Business Profile Setup',
          description: 'Your local visibility setup is saved. The dashboard tracker will show any business details and admin review steps still needed. Stripe payment comes after approval.'
        });
      }
    }

    if (hasAutomation && (!selectedServices || !selectedServices.some((service) => service.id.includes('automation')))) {
      notes.push({
        icon: Cog,
        title: 'Business Automation Setup',
        description: 'Your automation interest is saved. Use the dashboard tracker to complete the remaining setup details and submit for review.'
      });
    }

    return notes;
  };

  const serviceNotes = getServiceNotes();
  const credentialDelivery = credentialResult?.credential_delivery;
  const shouldShowSecureSetupEmailNotice = status === 'complete'
    && credentialDelivery?.email
    && credentialDelivery?.email_status === 'sent';

  return (
    <div className="text-center max-w-2xl mx-auto">
      {showPageAccessInfo && <SocialPageAccessModal onClose={() => setShowPageAccessInfo(false)} />}
      <div className="flex justify-center mb-6">
        <CheckCircle className="w-16 h-16 text-scale-blue" />
      </div>

      <h2 className="text-3xl font-bold text-white mb-4">
        {status === 'complete' ? 'You\'re All Set!' : 'Setup Complete!'}
      </h2>

      <p className="text-lg text-gray-300 mb-8">
        {status === 'complete'
          ? 'Your account has been provisioned. Next, connect your platforms and finish the dashboard setup tracker.'
          : 'Thank you for choosing Scale Small.AI! Click below to finalize your account setup.'}
      </p>

      {shouldShowSecureSetupEmailNotice && (
        <div className="bg-[#22c55e]/10 border border-[#22c55e]/40 p-5 rounded-lg mb-8 text-left">
          <div className="flex items-start gap-3">
            <Mail className="w-5 h-5 text-[#86efac] mt-1 flex-shrink-0" />
            <div>
              <h3 className="font-semibold text-white">Your secure setup link was emailed</h3>
              <p className="mt-1 text-sm leading-6 text-[#bbf7d0]">
                Check {credentialDelivery.email} for a one-time sign-in link. It opens Connect in the Scale Small AI dashboard; then connect each platform with the account that manages the business page or company page.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Service-specific setup notes */}
      {serviceNotes.length > 0 && (
        <div className="bg-scale-darker bg-opacity-50 backdrop-blur-sm border border-gray-700 p-6 rounded-lg mb-8">
          <h3 className="font-semibold text-white mb-6">What Happens Next</h3>
          <div className="space-y-6">
            {serviceNotes.map((note, index) => {
              const Icon = note.icon;
              return (
                <div key={index} className="flex items-start text-left">
                  <div className="w-10 h-10 bg-scale-blue/10 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <Icon className="w-5 h-5 text-scale-blue" />
                  </div>
                  <div>
                    <h4 className="font-medium text-white mb-2">{note.title}</h4>
                    <p className="text-gray-300 text-sm">{note.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Post-provisioning links — show after WF11 completes */}
      {status === 'complete' && (
        <div className="bg-scale-darker bg-opacity-50 backdrop-blur-sm border border-scale-blue/30 p-6 rounded-lg mb-8">
          <h3 className="font-semibold text-white mb-4">Your Account Links</h3>
          <div className="space-y-3">
            <a
              href={CONNECT_APP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between p-4 bg-scale-blue/10 border border-scale-blue/30 rounded-lg hover:bg-scale-blue/20 transition-colors"
            >
              <div className="text-left">
                <h4 className="font-medium text-white">Connect Your Platforms</h4>
                <p className="text-gray-400 text-sm">Link your social media accounts, Google Business Profile, Search Console, and job photo system</p>
                <p className="mt-2 text-xs text-[#bbf7d0]">
                  <strong className="text-[#22c55e]">Facebook & LinkedIn:</strong> open this link in an incognito window or Chrome profile where the page admin account is signed in. Sign in with your normal Scale Small AI login, then use the Facebook or LinkedIn popup with the account that manages the business/company page.
                </p>
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setShowPageAccessInfo(true);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      event.stopPropagation();
                      setShowPageAccessInfo(true);
                    }
                  }}
                  className="mt-2 inline-flex cursor-pointer text-xs font-black underline underline-offset-4 text-[#22c55e]"
                >
                  Click here for more info
                </span>
              </div>
              <ExternalLink className="w-5 h-5 text-scale-blue flex-shrink-0" />
            </a>
            <a
              href="https://dashboard.scalesmall.ai"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between p-4 bg-scale-blue/10 border border-scale-blue/30 rounded-lg hover:bg-scale-blue/20 transition-colors"
            >
              <div className="text-left">
                <h4 className="font-medium text-white">View Your Dashboard</h4>
                <p className="text-gray-400 text-sm">Monitor your posts, engagement metrics, and content pipeline</p>
              </div>
              <ExternalLink className="w-5 h-5 text-scale-blue flex-shrink-0" />
            </a>
          </div>
        </div>
      )}

      {/* General next steps */}
      {status !== 'complete' && (
        <div className="bg-scale-darker bg-opacity-50 backdrop-blur-sm border border-gray-700 p-6 rounded-lg mb-8">
          <h3 className="font-semibold text-white mb-4">General Next Steps</h3>
          <ul className="text-left space-y-3">
            <li className="flex items-start">
              <span className="w-6 h-6 bg-scale-blue text-scale-dark rounded-full flex items-center justify-center text-sm mr-3 flex-shrink-0 font-semibold">1</span>
              <span className="text-gray-300">Open Connect and choose only the platforms, Search Console access, photo source, and CRM you want Scale Small AI to use.</span>
            </li>
            <li className="flex items-start">
              <span className="w-6 h-6 bg-scale-blue text-scale-dark rounded-full flex items-center justify-center text-sm mr-3 flex-shrink-0 font-semibold">2</span>
              <span className="text-gray-300">Use Save and exit if you need to return later, or Finish and continue when your selected connections are ready.</span>
            </li>
            <li className="flex items-start">
              <span className="w-6 h-6 bg-scale-blue text-scale-dark rounded-full flex items-center justify-center text-sm mr-3 flex-shrink-0 font-semibold">3</span>
              <span className="text-gray-300">Land in the dashboard Home tracker, complete anything open, and wait for final admin review before services start.</span>
            </li>
          </ul>
        </div>
      )}

      {/* Error display */}
      {status === 'error' && errorMessage && (
        <div className="bg-red-500/10 border border-red-500/30 p-4 rounded-lg mb-8 text-left">
          <p className="text-red-400 text-sm">
            <strong>Error:</strong> {errorMessage}
          </p>
          <p className="text-gray-400 text-xs mt-2">
            Your selections have been saved. Contact support@scalesmall.ai if this persists.
          </p>
        </div>
      )}

      {/* Action Button */}
      <div className="mb-8">
        {status === 'complete' ? (
          <p className="text-scale-blue font-medium">
            All client-side onboarding is complete. Continue with platform connections when ready.
          </p>
        ) : (
          <button
            onClick={handleSaveAndProvision}
            disabled={status === 'saving' || status === 'provisioning'}
            className="w-full max-w-md mx-auto flex items-center justify-center px-8 py-4 bg-scale-blue text-scale-dark rounded-lg hover:bg-white transition-colors font-semibold text-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {status === 'saving' ? (
              <><Loader2 className="w-5 h-5 mr-3 animate-spin" />Saving selections...</>
            ) : status === 'provisioning' ? (
              <><Loader2 className="w-5 h-5 mr-3 animate-spin" />Setting up your account...</>
            ) : status === 'error' ? (
              <><Save className="w-5 h-5 mr-3" />Try Again</>
            ) : (
              <><Save className="w-5 h-5 mr-3" />Complete Setup</>
            )}
          </button>
        )}
      </div>

      <p className="text-sm text-gray-400">
        Need help? Contact us at{' '}
        <a href="mailto:support@scalesmall.ai" className="text-scale-blue hover:text-white transition-colors">
          support@scalesmall.ai
        </a>
      </p>
    </div>
  );
}
